import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import {
  TEDEKKontodelREDOVISN_TYPEnum,
  TEDLRFakturaAttestAttestTypEnum,
} from '../../../../../../globalQueryTypes';
import { IFieldValidation } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { MOMSSATS_TOLERANS_DEFAULT_VALUE } from '../Consts/FakturadelConsts';
import { isNumberWithinMarginOfError } from './FakturadelUtils';

const currencyFormatter = findFormatter('TEDFormatCurr');

const getChangePreselectedMomssatsError = (
  fieldName: string,
  konteringsforslagId: string
): IFieldValidation => {
  return {
    type: 'Warning',
    fieldName: fieldName,
    path: ['Faktura.Konteringsforslag', konteringsforslagId],
    message: 'Avvikande momssats är vald',
  };
};

export const validateUtlandskChangePreselectedMomssats = (
  konteringsforslagId: string,
  utlLeverantorMomsmallBenamning: string | undefined,
  utlMomsmallBenamning: string | undefined
): IFieldValidation | undefined => {
  if (
    utlLeverantorMomsmallBenamning &&
    utlMomsmallBenamning &&
    utlLeverantorMomsmallBenamning !== utlMomsmallBenamning
  ) {
    const error: IFieldValidation = getChangePreselectedMomssatsError(
      'UtlandsMomsMall',
      konteringsforslagId
    );
    return error;
  }
  return undefined;
};

export const validateSvenskChangePreselectedMomssats = (
  ingMomsSats: number | null,
  kalkMomssats: number | null | undefined,
  hasMomspliktigKoddel: boolean,
  hasValidTolkadMomsats: boolean | undefined | null,
  konteringsforslagId: string
): IFieldValidation | undefined => {
  if (
    hasMomspliktigKoddel &&
    ingMomsSats &&
    kalkMomssats &&
    hasValidTolkadMomsats &&
    !isNumberWithinMarginOfError(
      ingMomsSats,
      MOMSSATS_TOLERANS_DEFAULT_VALUE,
      kalkMomssats
    )
  ) {
    const error: IFieldValidation = getChangePreselectedMomssatsError(
      'IngMoms',
      konteringsforslagId
    );
    return error;
  }

  return undefined;
};

const getDeletePreselectedMomssatsError = (
  fieldName: string,
  konteringsforslagId: string
): IFieldValidation => {
  return {
    type: 'Error',
    fieldName: fieldName,
    path: ['Faktura.Konteringsforslag', konteringsforslagId],
    message: 'Ingående momssats saknar värde',
  };
};

const getDeletePreselectedMomssatsWarning = (
  fieldName: string,
  konteringsforslagId: string
): IFieldValidation => {
  return {
    type: 'Warning',
    fieldName: fieldName,
    path: ['Faktura.Konteringsforslag', konteringsforslagId],
    message: 'Ingående momssats saknar värde',
  };
};

export const validateUtlandskDeletePreselectedMomssats = (
  konteringsforslagId: string,
  utlMomsSats: number | null | undefined,
  utlLeverantorMomsmallId: string | undefined,
  attestTyp: TEDLRFakturaAttestAttestTypEnum
): IFieldValidation | undefined => {
  if (utlLeverantorMomsmallId != null && utlMomsSats == null) {
    if (attestTyp === TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST) {
      return getDeletePreselectedMomssatsWarning(
        'UtlandsMomsMall',
        konteringsforslagId
      );
    } else {
      return getDeletePreselectedMomssatsError(
        'UtlandsMomsMall',
        konteringsforslagId
      );
    }
  }

  return undefined;
};

export const validateSvenskDeletePreselectedMomssats = (
  ingMomsSats: number | null,
  hasMomspliktigKoddel: boolean,
  hasValidTolkadMomsats: boolean | undefined | null,
  konteringsforslagId: string,
  skipMomsValidation: boolean
): IFieldValidation | undefined => {
  if (
    !skipMomsValidation &&
    hasMomspliktigKoddel &&
    hasValidTolkadMomsats &&
    ingMomsSats == null
  ) {
    return getDeletePreselectedMomssatsError('IngMoms', konteringsforslagId);
  }
  return undefined;
};

export const validateNoMomssatsWhenTolkatMomsbeloppIsZero = (
  ingMomsSats: number | null,
  hasMomspliktigKoddel: boolean,
  tolkatMomsbelopp: number,
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  konteringsforslagId: string,
  skipTolkatMomsbeloppIsZeroValidation: boolean
): IFieldValidation | undefined => {
  if (
    hasMomspliktigKoddel &&
    tolkatMomsbelopp === 0 &&
    ingMomsSats != null &&
    !skipTolkatMomsbeloppIsZeroValidation
  ) {
    const error: IFieldValidation = {
      type:
        attestTyp === TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          ? 'Error'
          : 'Warning',
      fieldName: 'IngMoms',
      path: ['Faktura.Konteringsforslag', konteringsforslagId],
      message:
        'Fakturans tolkade momsbelopp är 0 kr och därför ska ingen momssats anges på konteringsraderna',
    };
    return error;
  }
  return undefined;
};

const getNoPreselectedMomssatsError = (
  fieldName: string,
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  konteringsforslagId: string,
  hasTouchedMomsMall: boolean
): IFieldValidation => {
  return {
    type:
      attestTyp === TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
        ? 'Warning'
        : 'Error',
    fieldName: fieldName,
    path: ['Faktura.Konteringsforslag', konteringsforslagId],
    message: 'Ingående momssats saknar värde',
    showAsHint:
      attestTyp === TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
        ? false
        : !hasTouchedMomsMall,
  };
};

export const validateSvenskNoPreselectedMomssats = (
  ingMomsSats: number | null,
  hasMomspliktigKoddel: boolean,
  hasTouchedIngMoms: boolean,
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  hasValidTolkadMomsats: boolean | undefined,
  tolkatMomsbelopp: number,
  konteringsforslagId: string
): IFieldValidation | undefined => {
  if (
    hasMomspliktigKoddel &&
    ingMomsSats == null &&
    !hasValidTolkadMomsats &&
    tolkatMomsbelopp !== 0
  ) {
    return getNoPreselectedMomssatsError(
      'IngMoms',
      attestTyp,
      konteringsforslagId,
      hasTouchedIngMoms
    );
  }

  return undefined;
};

export const validateUtlandskNoPreselectedMomssats = (
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  hasTouchedUtgandeMoms: boolean,
  konteringsforslagId: string,
  utlMomsSats: number | null | undefined,
  utlLeverantorMomsmallId: string | null | undefined
) => {
  if (utlLeverantorMomsmallId == null && utlMomsSats == null) {
    return getNoPreselectedMomssatsError(
      'UtlandsMomsMall',
      attestTyp,
      konteringsforslagId,
      hasTouchedUtgandeMoms
    );
  }
  return undefined;
};

export const validateSvenskIngaendeMomsbeloppGreaterThanTolkatMomsbelopp = (
  ingaendeMomsbelopp: number,
  hasMomspliktigKoddel: boolean,
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  tolkatMomsbelopp: number,
  momsbeloppTolerans: number,
  konteringsforslagId: string
): IFieldValidation | undefined => {
  if (
    hasMomspliktigKoddel &&
    Math.abs(ingaendeMomsbelopp) >
      Math.abs(tolkatMomsbelopp) + Math.abs(momsbeloppTolerans) &&
    tolkatMomsbelopp !== 0
  ) {
    return {
      type:
        attestTyp === TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          ? 'Error'
          : 'Warning',
      fieldName: 'IngMoms',
      path: ['Faktura.Konteringsforslag', konteringsforslagId],
      message: `Konterat momsbelopp överstiger tolkat momsbelopp på fakturan (${currencyFormatter.displayer.display(
        tolkatMomsbelopp,
        true,
        'Decimals="2"'
      )})`,
    };
  }

  return undefined;
};

export const validateUtlandskMomsbeloppGreaterThanTolkatMomsbelopp = (
  utlMoms: number,
  takbelopp: number,
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  konteringsforslagId: string
): IFieldValidation | undefined => {
  if (
    attestTyp === TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST &&
    Math.abs(utlMoms) > Math.abs(takbelopp)
  ) {
    return {
      type: 'Error',
      fieldName: 'IngMoms',
      path: ['Faktura.Konteringsforslag', konteringsforslagId],
      message: `Konterat momsbelopp (${utlMoms}) överstiger fakturans takbeloppet (${currencyFormatter.displayer.display(
        takbelopp,
        true,
        'Decimals="2"'
      )})`,
    };
  }

  return undefined;
};

export const validatePeriodiseringRedovisningstyp = (
  redovisningstyp: TEDEKKontodelREDOVISN_TYPEnum | undefined,
  id: string
): IFieldValidation | undefined => {
  if (redovisningstyp === TEDEKKontodelREDOVISN_TYPEnum.STALLNINGSKONTO) {
    return {
      fieldName: id + ' ' + 1,
      message: 'Balanskonton kan inte periodiseras',
      path: ['Faktura', 'Konteringsforslag', id],
      type: 'Error',
    };
  }
  return undefined;
};
